/**
 * The Inspector: the right region of the "Write" studio. One panel with three
 * shapes, driven by the selected pipeline node:
 *
 *   SECTION  the rich one: Type, Format (an EDITABLE control offering the three ways
 *            our AI turns the sources into this section; changing it applies on the next
 *            regenerate), title, purpose, bound
 *            sources (colour-matched chips), an editable per-section AI INSTRUCTION
 *            plus a "Regenerate this section" button (the mission lever: re-render just
 *            this block from fresh material + the instruction), a validation readout
 *            derived from the real block, and the block's editing controls (rename /
 *            rewrite / keep-dismiss / reorder) via the existing {@link BlockCard}.
 *   SOURCE   name, provider/channel, query/handle, freshness/cadence, its live link,
 *            and "feeds these sections" chips (the reverse binding).
 *   ISSUE    the subject line, plus an honest note that sender config (preview text,
 *            from-name, footer) is per-issue and coming next. Display only for E1.
 *
 * Nothing selected falls back to the ISSUE shape, so the panel is never blank.
 */
import { useMemo } from 'react';
import { Button, Field, Notification } from '@nuff/ui/react';

import type { Newsletter, Treatment } from '@nuff/types';
import type { ContentBlock, Issue, MissedLikedNewsletter, SeededInstruction } from '../../api';
import { BlockCard, type BlockFocusTarget } from './BlockCard';
import { isHttpUrl } from './IssueRenderer';
import type { PipelineSectionNode, PipelineSource, SelectedNode } from './pipeline';
import {
  blockById,
  freshnessReadout,
  offeredTreatment,
  OFFERED_TREATMENT_LABEL,
  OFFERED_TREATMENT_QUALIFIER,
  OFFERED_TREATMENTS,
  roleLabel,
  sectionNodeById,
  sectionsForSource,
  sourceChannelLabel,
  sourceColorMap,
  sourceDisplayName,
  sourceQuery,
  sourceUrl,
  treatmentRole,
  volumeReadout,
} from './pipeline';

/** The honest "not editable yet" hint on the still-inert bound-sources chips (E3b seam). */
const NOT_EDITABLE_HINT = 'Changing which sources feed this section is coming next';

/**
 * The treatment select options: the FOUR genuinely-distinct behaviours the writer produces
 * (a deterministic no-AI roundup, plus a quick digest, a short take, and a deeper
 * commentary). Labelled plainly, never the raw enum.
 */
const TREATMENT_OPTIONS = OFFERED_TREATMENTS.map((treatment) => ({
  value: treatment,
  label: OFFERED_TREATMENT_LABEL[treatment],
}));

/**
 * Static starter phrases that populate/append the instruction with one click, so the
 * blank box reads as a visible lever rather than an empty prompt. Plain, house-style
 * copy, no em dashes. (Blueprint-seeded starters from liked newsletters are E2.1.)
 */
const STARTER_INSTRUCTIONS = [
  'Make it punchier',
  'Lead with the numbers',
  'More context, less spin',
  'Shorten it',
  'Warmer tone',
] as const;

/** The honest caution under the regenerate button, mirroring the whole-issue REGEN_HINT. */
const SECTION_REGEN_CAUTION =
  'This rewrites the section from its sources and replaces any edits you made here.';

/** One seeded section in the roll-up: enough to name it and jump to it. */
export interface SeedRollupEntry {
  readonly sectionId: string;
  /** The section's heading, shown as the navigable label. */
  readonly title: string;
  /** The pipeline node id (block id) to select when the owner jumps to it. */
  readonly nodeId: string;
}

/**
 * The opt-in "draft starter instructions from the newsletters you love" surface (E2.1),
 * owned by the studio and rendered in a written section's AI-instructions lever. It is a
 * WHOLE-ISSUE action: one run drafts a starter for every written section, and a persistent
 * roll-up lets the owner jump to each and keep or dismiss it. It is NEVER auto-run.
 * Everything here is honest: a draft is attributed to the liked newsletter it came from,
 * and any liked newsletter we could not read is named in `missed` rather than implied away.
 */
export interface SeedUx {
  readonly state: 'idle' | 'loading' | 'done';
  readonly error: string | null;
  /** How many liked newsletters the owner has configured; 0 guides them to add some. */
  readonly likedCount: number;
  /** True when the last run produced no usable starters at all (honest failure line). */
  readonly ranEmpty: boolean;
  /** The liked newsletters we could not read on the last run, named honestly. */
  readonly missed: readonly MissedLikedNewsletter[];
  /** Still-undecided drafts across the issue: the persistent, navigable roll-up. */
  readonly pending: readonly SeedRollupEntry[];
  /** The section a starter was just kept for, so we can confirm the apply step (#4). */
  readonly keptSectionId: string | null;
  /** The pending (not-yet-kept-or-dismissed) draft for THIS section, or null. */
  readonly seedFor: (sectionId: string) => SeededInstruction | null;
  /** Trigger a whole-issue seed run (disabled while in flight). */
  readonly onSeed: () => void;
  /** Keep a draft: it fills the editable instruction above and persists. */
  readonly onKeep: (seed: SeededInstruction) => void;
  /** Dismiss a draft without using it. */
  readonly onDismiss: (sectionId: string) => void;
  /** Jump the inspector to a seeded section (by its pipeline node id). */
  readonly onNavigate: (nodeId: string) => void;
  /** Route to Basics, under About this newsletter, where liked newsletters live. */
  readonly onGoToAbout: () => void;
}

/** The seed action's copy: a short button label, with the full framing in the hint. */
const SEED_BUTTON_LABEL = 'Draft starter instructions for the whole issue';
const SEED_BUTTON_BUSY = 'Reading the newsletters you love…';
const SEED_HINT =
  'We’ll read the newsletters you love and draft a starter instruction for each ' +
  'written section across the issue, for you to keep or dismiss. Takes a few seconds.';

/** An honest one-liner about any liked newsletters we could not read, or '' when none. */
function missedNote(seed: SeedUx): string {
  if (seed.missed.length === 0) {
    return '';
  }
  const labels = seed.missed.map((m) => m.label).join(', ');
  return ` We couldn't read ${String(seed.missed.length)} of your ${String(seed.likedCount)}: ${labels}.`;
}

/**
 * The opt-in seed block inside a written section's AI-instructions lever: a whole-issue
 * trigger with framing, the reviewable draft card for THIS section (keep / dismiss), a
 * one-line confirmation after a keep (pointing at Regenerate), a PERSISTENT navigable
 * roll-up of every still-undecided section, and honest lines for what we could not read.
 * When the owner has no liked newsletters, it guides them to the real place to add some.
 */
function SeedInstructions({
  seed,
  sectionId,
  busy,
}: {
  readonly seed: SeedUx;
  readonly sectionId: string;
  readonly busy: boolean;
}): React.JSX.Element {
  if (seed.likedCount === 0) {
    return (
      <div className="nlv__insp-seed">
        <p className="nlv__insp-seed-note">
          Add newsletters you love on Basics, under About this newsletter, and we'll draft
          starter instructions from them.
        </p>
        <Button variant="ghost" size="sm" onClick={seed.onGoToAbout}>
          Go to Basics
        </Button>
      </div>
    );
  }
  const pending = seed.seedFor(sectionId);
  const loading = seed.state === 'loading';
  const done = seed.state === 'done';
  return (
    <div className="nlv__insp-seed">
      <p className="nlv__insp-seed-note">{SEED_HINT}</p>
      <Button
        variant="secondary"
        size="sm"
        onClick={seed.onSeed}
        disabled={busy || loading}
        loading={loading}
      >
        {loading ? SEED_BUTTON_BUSY : SEED_BUTTON_LABEL}
      </Button>
      {seed.error !== null && <Notification variant="error">{seed.error}</Notification>}

      {/* The reviewable draft for THIS section. */}
      {done && pending !== null && (
        <div className="nlv__insp-seed-card">
          <p className="nlv__insp-seed-text">{pending.instruction}</p>
          <p className="nlv__insp-seed-attr">Drafted from {pending.sourceLabel}</p>
          <div className="nlv__insp-seed-actions">
            <Button variant="primary" size="sm" onClick={() => seed.onKeep(pending)}>
              Keep
            </Button>
            <Button variant="ghost" size="sm" onClick={() => seed.onDismiss(sectionId)}>
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {/* After a keep the card is gone; confirm the apply step points at Regenerate. */}
      {done && seed.keptSectionId === sectionId && pending === null && (
        <p className="nlv__insp-seed-note" role="status" aria-live="polite">
          Added to this section's instructions. Regenerate this section to see it.
        </p>
      )}

      {/* The PERSISTENT whole-issue roll-up: reachable no matter which section shows. */}
      {done && seed.pending.length > 0 && (
        <div className="nlv__insp-seed-rollup" role="status" aria-live="polite">
          <p className="nlv__insp-seed-note">
            Starter drafts ready for {String(seed.pending.length)}{' '}
            {seed.pending.length === 1 ? 'section' : 'sections'} from the newsletters you love.
            Jump to any to keep or dismiss it.
            {missedNote(seed)}
          </p>
          <div className="nlv__insp-seed-list">
            {seed.pending.map((entry) => (
              <Button
                key={entry.sectionId}
                variant="ghost"
                size="sm"
                onClick={() => seed.onNavigate(entry.nodeId)}
              >
                {entry.title}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Nothing readable: one clear clause (never a double "couldn't"). */}
      {done && seed.ranEmpty && (
        <p className="nlv__insp-seed-note" role="status">
          {seed.missed.length > 0
            ? `We couldn't read the newsletters you love: ${seed.missed
                .map((m) => m.label)
                .join(', ')}.`
            : "We couldn't draft any starters just now."}
        </p>
      )}
    </div>
  );
}

/** One derived validation row: a plain honest check over the real block, never faked. */
interface Check {
  readonly label: string;
  readonly ok: boolean;
  readonly detail: string;
}

/** Honest validation for a section's block, derived from its real content (no green faking). */
function blockChecks(block: ContentBlock | null): Check[] {
  if (block === null) {
    return [{ label: 'Has content', ok: false, detail: 'not drafted yet' }];
  }
  const refs = block.sourceRefs ?? [];
  const bodyHasLink = /\]\(https?:\/\//.test(block.body);
  const hasBody = block.body.trim().length > 0;
  const checks: Check[] = [
    {
      label: 'Has content',
      ok: hasBody,
      detail: hasBody ? 'written' : 'empty',
    },
  ];
  if (block.role === 'links' || refs.length > 0) {
    const resolvable = refs.filter((ref) => isHttpUrl(ref.url)).length;
    checks.push({
      label: 'Links checked',
      ok: refs.length > 0 && resolvable === refs.length,
      detail: refs.length > 0 ? `${String(resolvable)} / ${String(refs.length)} ok` : 'none',
    });
  } else {
    checks.push({
      label: 'Links',
      ok: true,
      detail: bodyHasLink ? 'woven in' : 'none',
    });
  }
  return checks;
}

function SectionInspector({
  node,
  block,
  railBlocks,
  keptCount,
  colorByKey,
  editError,
  busy,
  focusTarget,
  onFocusHandled,
  highlightId,
  onHoverChange,
  onToggleKept,
  onMove,
  onEditTitle,
  onEditBody,
  onEditInstruction,
  onEditTreatment,
  onRegenerateSection,
  regeneratingBlockId,
  regenStatus,
  seed,
}: {
  readonly node: PipelineSectionNode;
  readonly block: ContentBlock | null;
  readonly railBlocks: readonly ContentBlock[];
  readonly keptCount: number;
  readonly colorByKey: ReadonlyMap<string, string>;
  readonly editError: string | null;
  readonly busy: boolean;
  readonly focusTarget: BlockFocusTarget | null;
  readonly onFocusHandled: () => void;
  readonly highlightId: string | null;
  readonly onHoverChange: (id: string | null) => void;
  readonly onToggleKept: (block: ContentBlock) => void;
  readonly onMove: (block: ContentBlock, direction: -1 | 1) => void;
  readonly onEditTitle: (id: string, title: string) => void;
  readonly onEditBody: (id: string, body: string) => void;
  readonly onEditInstruction: (sectionId: string, text: string) => void;
  readonly onEditTreatment: (sectionId: string, treatment: Treatment) => void;
  readonly onRegenerateSection: (block: ContentBlock) => Promise<void>;
  /** The opt-in seed-from-liked-newsletters surface for this section (E2.1). */
  readonly seed: SeedUx;
  /** The block id whose per-section regenerate is in flight (owned by the studio). */
  readonly regeneratingBlockId: string | null;
  /** The most recent regenerate outcome for a block (updated / no-op / error). */
  readonly regenStatus: {
    readonly blockId: string;
    readonly kind: 'updated' | 'noop' | 'error';
    readonly message: string;
  } | null;
}): React.JSX.Element {
  const checks = blockChecks(block);
  const section = node.section;
  // The studio owns the regenerate in-flight state (so it can also dim the block + block
  // the whole-issue Regenerate), so THIS section is regenerating when its block matches.
  const regenerating = block !== null && regeneratingBlockId === block.id;
  // Show the regenerate outcome only for THIS block (a selection change moves on from it).
  const thisRegenStatus =
    block !== null && regenStatus?.blockId === block.id ? regenStatus : null;
  // Append a starter chip to the instruction (space-joined), so chips compose rather
  // than overwrite; a controlled textarea reflects it via onEditInstruction.
  const appendInstruction = (chip: string): void => {
    if (section === null) {
      return;
    }
    const current = (section.instruction ?? '').trim();
    onEditInstruction(section.sectionId, current.length > 0 ? `${current} ${chip}` : chip);
  };
  // This block's index among the KEPT blocks, so the BlockCard's reorder arrows know
  // the ends (matching how the studio rail computed it before editing moved here).
  const keptIndex =
    block !== null && block.status !== 'dismissed'
      ? railBlocks.filter((candidate) => candidate.status !== 'dismissed').indexOf(block)
      : -1;

  return (
    <div className="nlv__insp-body">
      <div className="nlv__insp-group">
        <p className="nlv__insp-eyebrow">Section</p>
        <h3 className="nlv__insp-title">{node.title}</h3>
        <p className="nlv__insp-meta">{roleLabel(node.role)}</p>
      </div>

      {/* The working lever LEADS the panel, above the inert read-only groups. */}
      {section !== null ? (
        <div className="nlv__insp-group nlv__insp-group--lever">
          <p className="nlv__insp-group-title">AI instructions</p>
          <p className="nlv__insp-hint">
            Tell our AI how to write this section. It steers voice and emphasis; it never invents
            facts.
          </p>
          <textarea
            className="nlv__insp-instruction"
            aria-label={`AI instruction for ${node.title}`}
            placeholder="For example: lead with the funding numbers and keep it brisk."
            value={section.instruction ?? ''}
            onChange={(event) => onEditInstruction(section.sectionId, event.target.value)}
          />
          <div className="nlv__insp-chips nlv__insp-chip-row">
            {STARTER_INSTRUCTIONS.map((chip) => (
              <button
                key={chip}
                type="button"
                className="nlv__insp-chip nlv__insp-chip--button"
                onClick={() => appendInstruction(chip)}
              >
                {chip}
              </button>
            ))}
          </div>
          <SeedInstructions seed={seed} sectionId={section.sectionId} busy={busy} />
          <p className="nlv__insp-hint nlv__insp-regen-caution">{SECTION_REGEN_CAUTION}</p>
          <Button
            variant="primary"
            className="nlv__insp-regen"
            onClick={() => (block !== null ? void onRegenerateSection(block) : undefined)}
            disabled={busy || block === null}
            loading={regenerating}
            title="Re-write just this section with our AI, using the instruction above."
          >
            {regenerating ? 'Regenerating this section…' : 'Regenerate this section'}
          </Button>
          {/* Regen outcome (updated / no fresher material / error) lives NEXT TO the
              button and is announced politely, not buried in the shared Edit group. */}
          <p
            className={`nlv__insp-regen-status${
              thisRegenStatus?.kind === 'error' ? ' nlv__insp-regen-status--error' : ''
            }`}
            role="status"
            aria-live="polite"
          >
            {thisRegenStatus?.message ?? ''}
          </p>
        </div>
      ) : (
        <div className="nlv__insp-group nlv__insp-group--lever">
          <p className="nlv__insp-group-title">AI instructions</p>
          <p className="nlv__insp-hint">
            This is a text block you write yourself, so it has no AI instruction. Edit its text
            below.
          </p>
        </div>
      )}

      <div className="nlv__insp-group">
        {section !== null ? (
          <>
            {/* The honest treatment knob: an EDITABLE @nuff/ui select offering the FOUR
                behaviours the writer truthfully produces distinct output for. The current
                treatment is folded onto its nearest offered option so the select always shows
                a valid, honest selection (a stored `data` reads as "Link roundup"); we persist
                the folded canonical value only when the owner changes it. It applies on the
                next per-section regenerate, cued below. */}
            <p className="nlv__insp-hint">Choose how our AI turns the sources into this section.</p>
            <Field
              label="Format"
              name={`treatment-${node.blockId}`}
              options={TREATMENT_OPTIONS}
              value={offeredTreatment(section.treatment)}
              onChange={(value) => {
                const next = OFFERED_TREATMENTS.find((treatment) => treatment === value);
                if (next !== undefined) {
                  onEditTreatment(section.sectionId, next);
                }
              }}
              hint={OFFERED_TREATMENT_QUALIFIER[offeredTreatment(section.treatment)]}
            />
            {/* A gentle, HONEST cue: shown only when the pick would actually change this
                block on regenerate (its render role differs from the block's current one),
                so we never promise a change that would not happen. Announced politely. */}
            <p className="nlv__insp-regen-status" role="status" aria-live="polite">
              {treatmentRole(section.treatment) !== node.role
                ? 'Regenerate this section to see the new treatment.'
                : ''}
            </p>
          </>
        ) : (
          <>
            <p className="nlv__insp-group-title">
              Format{' '}
              <span className="nlv__insp-group-note">how our AI writes this section</span>
            </p>
            <p className="nlv__insp-hint">
              This block isn&apos;t built from sources, you write its text directly.
            </p>
          </>
        )}
      </div>

      {node.summary !== null && (
        <div className="nlv__insp-group">
          <p className="nlv__insp-group-title">Purpose</p>
          <p className="nlv__insp-text">{node.summary}</p>
        </div>
      )}

      <div className="nlv__insp-group">
        <p className="nlv__insp-group-title">Bound sources</p>
        {node.section !== null && node.section.sources.length > 0 ? (
          <div className="nlv__insp-chips">
            {node.section.sources.map((ref, index) => {
              const name = sourceDisplayName(ref);
              const color = colorByKey.get(name);
              return (
                <span
                  key={index}
                  className="nlv__insp-chip nlv__insp-chip--static"
                  title={NOT_EDITABLE_HINT}
                >
                  {color !== undefined && (
                    <span
                      className="nlv__map-dot"
                      style={{ background: color }}
                      aria-hidden="true"
                    />
                  )}
                  {name}
                </span>
              );
            })}
          </div>
        ) : (
          <p className="nlv__insp-hint">No source. This section is AI-written or static.</p>
        )}
      </div>

      <div className="nlv__insp-group">
        <p className="nlv__insp-group-title">Edit this section</p>
        {editError !== null && <Notification variant="error">{editError}</Notification>}
        {block !== null ? (
          <BlockCard
            block={block}
            index={keptIndex}
            count={keptCount}
            busy={busy}
            focusTarget={focusTarget}
            onFocusHandled={onFocusHandled}
            highlightId={highlightId}
            onHoverChange={onHoverChange}
            onToggleKept={onToggleKept}
            onMove={onMove}
            onEditTitle={onEditTitle}
            onEditBody={onEditBody}
          />
        ) : (
          <p className="nlv__insp-hint">This section has not been drafted into a block yet.</p>
        )}
      </div>

      <div className="nlv__insp-group">
        <p className="nlv__insp-group-title">Validation</p>
        <ul className="nlv__insp-valid">
          {checks.map((check) => (
            <li key={check.label}>
              <span
                className={`nlv__insp-check${check.ok ? ' nlv__insp-check--ok' : ' nlv__insp-check--warn'}`}
                aria-hidden="true"
              >
                {check.ok ? '✓' : '!'}
              </span>
              {check.label}
              <span className="nlv__insp-check-detail">{check.detail}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function SourceInspector({
  source,
  sectionNodes,
  onSelect,
}: {
  readonly source: PipelineSource;
  readonly sectionNodes: readonly PipelineSectionNode[];
  readonly onSelect: (node: SelectedNode) => void;
}): React.JSX.Element {
  const url = sourceUrl(source.ref);
  const freshness = freshnessReadout(source.ref.metrics);
  const feeds = sectionsForSource(source.key, sectionNodes);

  return (
    <div className="nlv__insp-body">
      <div className="nlv__insp-group">
        <p className="nlv__insp-eyebrow">Source</p>
        <h3 className="nlv__insp-title">
          <span className="nlv__map-dot" style={{ background: source.color }} aria-hidden="true" />
          {source.name}
        </h3>
        <p className="nlv__insp-meta">{sourceChannelLabel(source.ref)}</p>
      </div>

      <div className="nlv__insp-group">
        <p className="nlv__insp-group-title">Query / handle</p>
        <p className="nlv__insp-text nlv__insp-mono">{sourceQuery(source.ref)}</p>
      </div>

      <div className="nlv__insp-group">
        <p className="nlv__insp-group-title">Freshness &amp; volume</p>
        <p className="nlv__insp-text">
          {freshness !== null ? `${freshness} · ` : ''}
          {volumeReadout(source.ref.metrics)}
        </p>
        {url !== null && (
          <p className="nlv__insp-text">
            <a
              className="nlv__issue-link"
              href={url}
              target="_blank"
              rel="noreferrer noopener"
            >
              Open source
            </a>
          </p>
        )}
      </div>

      <div className="nlv__insp-group">
        <p className="nlv__insp-group-title">Feeds these sections</p>
        {feeds.length > 0 ? (
          <div className="nlv__insp-chips">
            {feeds.map((node) => (
              <Button
                key={node.blockId}
                variant="ghost"
                size="sm"
                onClick={() => onSelect({ kind: 'section', id: node.blockId })}
              >
                {node.title}
              </Button>
            ))}
          </div>
        ) : (
          <p className="nlv__insp-hint">Not bound to any section yet.</p>
        )}
      </div>
    </div>
  );
}

function IssueInspector({
  issue,
  newsletter,
}: {
  readonly issue: Issue | null;
  readonly newsletter: Newsletter;
}): React.JSX.Element {
  return (
    <div className="nlv__insp-body">
      <div className="nlv__insp-group">
        <p className="nlv__insp-eyebrow">Issue settings</p>
        <h3 className="nlv__insp-title">Issue</h3>
        <p className="nlv__insp-meta">{newsletter.name}</p>
      </div>

      <div className="nlv__insp-group">
        <p className="nlv__insp-group-title">Subject line</p>
        <p className="nlv__insp-text">
          {issue !== null && issue.subject.length > 0 ? issue.subject : 'Drafted with your issue.'}
        </p>
      </div>

      <div className="nlv__insp-group">
        <p className="nlv__insp-group-title">Sender &amp; footer</p>
        {/* Sender name + reply-to are now newsletter-level config, edited in Basics; only
            per-issue subject/preview remain auto for now. Keep the note honest about that. */}
        <p className="nlv__insp-hint">
          The sender name and reply-to address are set in Basics and apply to every issue. Subject
          and preview text are still written for you per issue.
        </p>
      </div>
    </div>
  );
}

export function Inspector({
  selected,
  sectionNodes,
  sources,
  issue,
  newsletter,
  railBlocks,
  keptCount,
  editError,
  busy,
  focusTarget,
  onFocusHandled,
  highlightId,
  onHoverChange,
  onToggleKept,
  onMove,
  onEditTitle,
  onEditBody,
  onEditInstruction,
  onEditTreatment,
  onRegenerateSection,
  regeneratingBlockId,
  regenStatus,
  seed,
  onSelect,
}: {
  readonly selected: SelectedNode | null;
  readonly sectionNodes: readonly PipelineSectionNode[];
  readonly sources: readonly PipelineSource[];
  readonly issue: Issue | null;
  readonly newsletter: Newsletter;
  readonly railBlocks: readonly ContentBlock[];
  readonly keptCount: number;
  readonly editError: string | null;
  readonly busy: boolean;
  readonly focusTarget: BlockFocusTarget | null;
  readonly onFocusHandled: () => void;
  readonly highlightId: string | null;
  readonly onHoverChange: (id: string | null) => void;
  readonly onToggleKept: (block: ContentBlock) => void;
  readonly onMove: (block: ContentBlock, direction: -1 | 1) => void;
  readonly onEditTitle: (id: string, title: string) => void;
  readonly onEditBody: (id: string, body: string) => void;
  readonly onEditInstruction: (sectionId: string, text: string) => void;
  readonly onEditTreatment: (sectionId: string, treatment: Treatment) => void;
  readonly onRegenerateSection: (block: ContentBlock) => Promise<void>;
  readonly regeneratingBlockId: string | null;
  readonly regenStatus: {
    readonly blockId: string;
    readonly kind: 'updated' | 'noop' | 'error';
    readonly message: string;
  } | null;
  readonly seed: SeedUx;
  readonly onSelect: (node: SelectedNode) => void;
}): React.JSX.Element {
  // Memoised so a block-edit keystroke (which re-renders the studio) does not rebuild
  // the colour lookup; it only changes when the distinct sources change.
  const colorByKey = useMemo(() => sourceColorMap(sources), [sources]);

  let content: React.JSX.Element;
  if (selected !== null && selected.kind === 'section') {
    const node = sectionNodeById(selected.id, sectionNodes);
    content =
      node !== null ? (
        <SectionInspector
          node={node}
          block={blockById(selected.id, railBlocks)}
          railBlocks={railBlocks}
          keptCount={keptCount}
          colorByKey={colorByKey}
          editError={editError}
          busy={busy}
          focusTarget={focusTarget}
          onFocusHandled={onFocusHandled}
          highlightId={highlightId}
          onHoverChange={onHoverChange}
          onToggleKept={onToggleKept}
          onMove={onMove}
          onEditTitle={onEditTitle}
          onEditBody={onEditBody}
          onEditInstruction={onEditInstruction}
          onEditTreatment={onEditTreatment}
          onRegenerateSection={onRegenerateSection}
          regeneratingBlockId={regeneratingBlockId}
          regenStatus={regenStatus}
          seed={seed}
        />
      ) : (
        <IssueInspector issue={issue} newsletter={newsletter} />
      );
  } else if (selected !== null && selected.kind === 'source') {
    const source = sources.find((candidate) => candidate.key === selected.id) ?? null;
    content =
      source !== null ? (
        <SourceInspector source={source} sectionNodes={sectionNodes} onSelect={onSelect} />
      ) : (
        <IssueInspector issue={issue} newsletter={newsletter} />
      );
  } else {
    content = <IssueInspector issue={issue} newsletter={newsletter} />;
  }

  return (
    <section className="nlv__panel nlv__inspector" aria-labelledby="nlv-insp-heading">
      <h2 id="nlv-insp-heading" className="nlv__panel-title nlv__u-visually-hidden">
        Inspector
      </h2>
      {content}
    </section>
  );
}
